import React from 'react';
import { Plus, Calendar, Clock, Users } from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { cn } from '../../../lib/utils';

interface Recipe {
  id: string;
  title: string;
  image?: string;
  prepTime: number;
  cookTime: number;
  servings: number;
  difficulty: 'Easy' | 'Medium' | 'Hard';
}

interface MealSlot {
  recipe?: Recipe;
  isEmpty: boolean;
}

interface DayPlan {
  date: string;
  dayName: string;
  meals: {
    breakfast: MealSlot;
    lunch: MealSlot;
    dinner: MealSlot;
  };
}

interface MealPlanGridProps {
  weekData: DayPlan[];
  onAddRecipe: (day: string, mealType: string) => void;
  onRecipeClick: (recipe: Recipe) => void;
  onRecipeRemove?: (day: string, mealType: string) => void;
  className?: string;
}

const MEAL_TYPES = [
  { key: 'breakfast', label: 'Breakfast', color: 'bg-orange-50 border-orange-200' },
  { key: 'lunch', label: 'Lunch', color: 'bg-blue-50 border-blue-200' },
  { key: 'dinner', label: 'Dinner', color: 'bg-purple-50 border-purple-200' },
];

export function MealPlanGrid({ 
  weekData, 
  onAddRecipe, 
  onRecipeClick, 
  onRecipeRemove,
  className 
}: MealPlanGridProps) {
  
  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const MealCard = ({ 
    meal, 
    day, 
    mealType, 
    mealConfig 
  }: { 
    meal: MealSlot; 
    day: string; 
    mealType: string; 
    mealConfig: typeof MEAL_TYPES[0];
  }) => {
    if (meal.isEmpty || !meal.recipe) {
      return (
        <Card className={cn('border-2 border-dashed border-gray-200 hover:border-warning transition-colors', mealConfig.color)}>
          <CardContent className="p-4">
            <Button
              variant="ghost"
              onClick={() => onAddRecipe(day, mealType)}
              className="w-full h-20 flex flex-col items-center justify-center text-gray-500 hover:text-warning hover:bg-transparent"
            >
              <Plus className="w-6 h-6 mb-1" />
              <span className="text-sm">Add {mealConfig.label}</span>
            </Button>
          </CardContent>
        </Card>
      );
    }

    const { recipe } = meal;
    const totalTime = recipe.prepTime + recipe.cookTime;

    return (
      <Card className={cn('border hover:shadow-md transition-all cursor-pointer group', mealConfig.color)}>
        <CardContent className="p-3">
          <div onClick={() => onRecipeClick(recipe)}>
            {/* Recipe Image */}
            {recipe.image ? (
              <div className="aspect-video rounded-lg overflow-hidden mb-3 bg-gray-100">
                <img 
                  src={recipe.image} 
                  alt={recipe.title}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200"
                />
              </div>
            ) : (
              <div className="aspect-video rounded-lg bg-gray-100 mb-3 flex items-center justify-center">
                <Calendar className="w-8 h-8 text-gray-400" />
              </div>
            )}

            {/* Recipe Info */}
            <h4 className="font-medium text-sm text-gray-900 line-clamp-2 mb-2 group-hover:text-warning transition-colors">
              {recipe.title}
            </h4>

            <div className="flex items-center justify-between text-xs text-gray-600">
              <div className="flex items-center">
                <Clock className="w-3 h-3 mr-1" />
                <span>{totalTime}m</span>
              </div>
              <div className="flex items-center">
                <Users className="w-3 h-3 mr-1" />
                <span>{recipe.servings}</span>
              </div>
            </div>
          </div>

          {/* Remove Button (on hover) */}
          {onRecipeRemove && (
            <Button
              variant="ghost"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                onRecipeRemove(day, mealType);
              }}
              className="w-full mt-2 text-xs text-gray-500 hover:text-red-600 hover:bg-red-50 opacity-0 group-hover:opacity-100 transition-opacity"
            >
              Remove
            </Button>
          )}
        </CardContent>
      </Card>
    );
  };

  return (
    <div className={cn('space-y-6', className)}>
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-semibold text-gray-900 font-header">
          Weekly Meal Plan
        </h2>
        <div className="text-sm text-gray-600">
          {weekData.length} days planned
        </div>
      </div>

      {/* Mobile View (Stacked Days) */}
      <div className="block lg:hidden space-y-6">
        {weekData.map((day) => (
          <Card key={day.date} className="border border-gray-200">
            <CardContent className="p-4">
              {/* Day Header */}
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="font-semibold text-gray-900 font-header">
                    {day.dayName}
                  </h3>
                  <p className="text-sm text-gray-600">
                    {formatDate(day.date)}
                  </p>
                </div>
              </div>

              {/* Meals for the day */}
              <div className="space-y-4">
                {MEAL_TYPES.map((mealConfig) => (
                  <div key={mealConfig.key}>
                    <h4 className="text-sm font-medium text-gray-700 mb-2">
                      {mealConfig.label}
                    </h4>
                    <MealCard
                      meal={day.meals[mealConfig.key as keyof typeof day.meals]}
                      day={day.date}
                      mealType={mealConfig.key}
                      mealConfig={mealConfig}
                    />
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Desktop View (Grid) */}
      <div className="hidden lg:block">
        {/* Grid Header */}
        <div className="grid grid-cols-8 gap-4 mb-4">
          <div className="font-medium text-gray-700"></div>
          {weekData.map((day) => (
            <div key={day.date} className="text-center">
              <div className="font-semibold text-gray-900 font-header">
                {day.dayName}
              </div>
              <div className="text-sm text-gray-600">
                {formatDate(day.date)}
              </div>
            </div>
          ))}
        </div>

        {/* Grid Content */}
        <div className="space-y-4">
          {MEAL_TYPES.map((mealConfig) => (
            <div key={mealConfig.key} className="grid grid-cols-8 gap-4">
              {/* Meal Type Label */}
              <div className="flex items-center">
                <span className="font-medium text-gray-700 text-sm">
                  {mealConfig.label}
                </span>
              </div>

              {/* Meal Cards for each day */}
              {weekData.map((day) => (
                <MealCard
                  key={`${day.date}-${mealConfig.key}`}
                  meal={day.meals[mealConfig.key as keyof typeof day.meals]}
                  day={day.date}
                  mealType={mealConfig.key}
                  mealConfig={mealConfig}
                />
              ))}
            </div>
          ))}
        </div>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 pt-4 border-t border-gray-200">
        <div className="text-center">
          <div className="text-2xl font-semibold text-gray-900 font-header">
            {weekData.reduce((acc, day) => {
              return acc + Object.values(day.meals).filter(meal => meal.recipe).length;
            }, 0)}
          </div>
          <div className="text-sm text-gray-600">Meals Planned</div>
        </div>
        <div className="text-center">
          <div className="text-2xl font-semibold text-gray-900 font-header">
            {21 - weekData.reduce((acc, day) => {
              return acc + Object.values(day.meals).filter(meal => meal.recipe).length;
            }, 0)}
          </div>
          <div className="text-sm text-gray-600">Meals Needed</div>
        </div>
        <div className="text-center">
          <div className="text-2xl font-semibold text-gray-900 font-header">
            {Math.round(weekData.reduce((acc, day) => {
              return acc + Object.values(day.meals)
                .filter(meal => meal.recipe)
                .reduce((mealAcc, meal) => mealAcc + (meal.recipe?.prepTime || 0) + (meal.recipe?.cookTime || 0), 0);
            }, 0) / 60)}h
          </div>
          <div className="text-sm text-gray-600">Total Cook Time</div>
        </div>
        <div className="text-center">
          <div className="text-2xl font-semibold text-gray-900 font-header">
            {weekData.reduce((acc, day) => {
              return acc + Object.values(day.meals)
                .filter(meal => meal.recipe)
                .reduce((servingAcc, meal) => servingAcc + (meal.recipe?.servings || 0), 0);
            }, 0)}
          </div>
          <div className="text-sm text-gray-600">Total Servings</div>
        </div>
      </div>
    </div>
  );
}